import { StyleSheet, Platform } from 'react-native';

const IS_WEB = Platform.OS === 'web';


const T = {
    navy: '#0f2039',
    navyLight: '#1a3355',
    navyMid: '#142a47',
    blue: '#2d8cf0',
    blueLight: '#5ba8f5',
    blueAccent: '#3b82f6',
    gold: '#f5a623',
    goldLight: '#fbbf24',
    cyan: '#06b6d4',
    green: '#10b981',
    red: '#ef4444',
    amber: '#f59e0b',
    violet: '#8b5cf6',
    // Neutrals
    white: '#ffffff',
    gray50: '#f8fafc',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray300: '#cbd5e1',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
    gray900: '#0f172a',
    // Glass surfaces
    glass: 'rgba(255,255,255,0.06)',
    glass2: 'rgba(255,255,255,0.03)',
    glassBorder: 'rgba(255,255,255,0.08)',
    glassBorder2: 'rgba(255,255,255,0.12)',
    // Radius — flat fintech look
    rSm: 3,
    rMd: 5,
    rLg: 8,
    rXl: 10,
    rFull: 9999,
};


const S = StyleSheet.create({
    root: { flex: 1, backgroundColor: T.navy },
    content: { padding: 16 },

    // Background orbs (web)
    bgOrbs: { position: 'absolute', top: 0, left: 0, right: 0, height: 400, pointerEvents: 'none' },
    orb: { position: 'absolute', borderRadius: 9999 },
    orb1: { width: 300, height: 300, top: -80, right: -60, backgroundColor: 'rgba(59,130,246,0.07)' },
    orb2: { width: 200, height: 200, top: 100, left: -60, backgroundColor: 'rgba(245,166,35,0.05)' },

    // Loading
    loadWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: T.navy },
    loadTxt: { color: T.gray400, fontSize: 12, marginTop: 10 },

    // Page header
    pageHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
    // Hero badge — mirrors .hero-badge
    heroBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
        backgroundColor: 'rgba(59,130,246,0.08)',
        borderWidth: 1, borderColor: 'rgba(59,130,246,0.2)',
        paddingHorizontal: 10, paddingVertical: 4,
        borderRadius: T.rFull,
        marginBottom: 6,
    },
    badgeDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#22c55e' },
    heroBadgeTxt: { fontSize: 9, fontWeight: '600', color: T.blueLight, letterSpacing: 0.5 },
    pageTitle: { fontSize: 20, fontWeight: '600', color: T.white, letterSpacing: -0.4 },
    pageSub: { fontSize: 11, color: T.gray400, marginTop: 2 },
    refreshBtn: {
        flexDirection: 'row', alignItems: 'center', gap: 5,
        backgroundColor: T.glass, borderWidth: 1, borderColor: T.glassBorder,
        paddingHorizontal: 10, paddingVertical: 6, borderRadius: T.rFull,
    },
    refreshTxt: { fontSize: 11, color: T.blueLight, fontWeight: '600' },

    // ── Metric card — mirrors .feature-card + glassmorphism
    metricCard: {
        width: IS_WEB ? 210 : 175,
        backgroundColor: T.glass,
        borderRadius: T.rLg,
        borderWidth: 1,
        padding: 12,
        position: 'relative',
        overflow: 'hidden',
    },
    metricAccentTop: { position: 'absolute', top: 0, left: 0, right: 0, height: 2 },
    metricHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
    metricIconBox: { width: 26, height: 26, borderRadius: T.rMd, alignItems: 'center', justifyContent: 'center' },
    metricActionChip: {
        flexDirection: 'row', alignItems: 'center', gap: 3,
        borderWidth: 1, borderRadius: T.rFull,
        paddingHorizontal: 7, paddingVertical: 2,
    },
    metricActionTxt: { fontSize: 9, fontWeight: '500' },
    metricLabel: { fontSize: 10, color: T.gray400, fontWeight: '500', marginBottom: 4 },
    metricValue: { fontSize: 17, fontWeight: '600', letterSpacing: -0.4 },
    metricFoot: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 8 },
    metricSub: { fontSize: 9, color: T.gray500, flex: 1 },
    metricWithdrawRow: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: T.glassBorder,
    },
    metricWithdrawTxt: { fontSize: 9, color: '#fca5a5', fontWeight: '600' },

    // ── Stats strip — mirrors .stats section feel
    statsStrip: {
        flexDirection: 'row',
        backgroundColor: T.glass,
        borderWidth: 1, borderColor: T.glassBorder,
        borderRadius: T.rMd,
        padding: 12, marginBottom: 12,
    },
    statItem: { flex: 1, alignItems: 'center' },
    statVal: { fontSize: 18, fontWeight: '600', color: T.white },
    statLbl: { fontSize: 9, color: T.gray500, marginTop: 2, textAlign: 'center' },
    statsDivider: { width: 1, backgroundColor: T.glassBorder, marginVertical: 2 },

    // ── Tab bar — mirrors .step-card tabs
    tabBar: {
        flexDirection: 'row',
        backgroundColor: T.navyMid,
        borderRadius: T.rMd,
        borderWidth: 1, borderColor: T.glassBorder,
        padding: 3, marginBottom: 14,
        position: 'relative', overflow: 'hidden',
    },
    tabIndicator: {
        position: 'absolute', top: 3, left: 3,
        height: '100%',
        backgroundColor: T.glass2,
        borderRadius: T.rMd - 2,
        // Blue glow like .btn-primary
        ...Platform.select({ web: { boxShadow: '0 0 12px rgba(45,140,240,0.25)' } }),
        borderWidth: 1, borderColor: 'rgba(45,140,240,0.2)',
    },
    tabBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 8, gap: 4 },
    tabLbl: { fontSize: 11, fontWeight: '600', color: T.gray500 },
    tabLblActive: { color: T.blueLight },

    // ── Toolbar
    toolbar: { gap: 8, marginBottom: 10 },
    searchWrap: {
        flexDirection: 'row', alignItems: 'center', gap: 8,
        backgroundColor: T.navyMid, borderWidth: 1, borderColor: T.glassBorder,
        borderRadius: T.rMd, paddingHorizontal: 10, paddingVertical: 8,
    },
    searchInput: { flex: 1, color: T.white, fontSize: 12 },
    filterChip: {
        paddingHorizontal: 10, paddingVertical: 5, borderRadius: T.rFull,
        backgroundColor: T.glass, borderWidth: 1, borderColor: T.glassBorder,
    },
    filterChipActive: {
        backgroundColor: 'rgba(45,140,240,0.12)',
        borderColor: 'rgba(45,140,240,0.3)',
    },
    filterChipTxt: { fontSize: 10, color: T.gray400, fontWeight: '600' },
    filterChipTxtActive: { color: T.blueLight },

    // ── Section tag — mirrors .section-tag
    sectionTag: {
        alignSelf: 'flex-start',
        backgroundColor: 'rgba(59,130,246,0.1)',
        borderWidth: 1, borderColor: 'rgba(59,130,246,0.2)',
        paddingHorizontal: 10, paddingVertical: 4,
        borderRadius: T.rFull,
    },
    sectionTagTxt: { fontSize: 9, fontWeight: '500', color: T.blueLight, letterSpacing: 1.5 },

    listHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
    listCount: { fontSize: 10, color: T.gray500 },

    // ── Pill
    pill: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        paddingHorizontal: 8, paddingVertical: 3,
        borderRadius: T.rFull, borderWidth: 1,
    },
    pillDot: { width: 5, height: 5, borderRadius: 3 },
    pillTxt: { fontSize: 8, fontWeight: '500', letterSpacing: 0.4 },

    // ── Empty state
    emptyState: { alignItems: 'center', paddingVertical: 36, gap: 8 },
    emptyIcon: { width: 52, height: 52, borderRadius: T.rMd, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
    emptyTitle: { fontSize: 14, fontWeight: '500', color: T.gray400 },
    emptySub: { fontSize: 11, color: T.gray600 },

    // ── Withdrawal card — mirrors .feature-card
    txCard: {
        backgroundColor: T.glass,
        borderRadius: T.rMd,
        borderWidth: 1, borderColor: T.glassBorder,
        padding: 12, marginBottom: 8,
        position: 'relative', overflow: 'hidden',
    },
    txCardPending: { borderColor: 'rgba(245,166,35,0.25)' },
    txAccentTop: { position: 'absolute', top: 0, left: 0, right: 0, height: 2 },
    txRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
    txAvatar: { width: 32, height: 32, borderRadius: T.rSm, alignItems: 'center', justifyContent: 'center' },
    txAvatarTxt: { fontSize: 13, fontWeight: '600' },
    txName: { fontSize: 12, fontWeight: '500', color: T.white },
    txPhone: { fontSize: 10, color: T.gray500, marginTop: 1 },
    txDate: { fontSize: 9, color: T.gray600, marginTop: 2 },
    txAmount: { fontSize: 13, fontWeight: '600' },
    txActions: { flexDirection: 'row', gap: 8, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: T.glassBorder },
    // Approve — mirrors .btn-primary
    approveBtn: {
        flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5,
        backgroundColor: T.green,
        paddingVertical: 8, borderRadius: T.rMd,
        ...Platform.select({ web: { boxShadow: '0 4px 15px rgba(16,185,129,0.35)' } }),
    },
    // Reject
    rejectBtn: {
        flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5,
        backgroundColor: 'rgba(239,68,68,0.18)',
        borderWidth: 1, borderColor: 'rgba(239,68,68,0.35)',
        paddingVertical: 8, borderRadius: T.rMd,
    },
    actionBtnTxt: { color: T.white, fontSize: 11, fontWeight: '500' },

    // ── Wallet card
    walletCard: {
        backgroundColor: T.glass,
        borderRadius: T.rMd, borderWidth: 1, borderColor: T.glassBorder,
        padding: 12, marginBottom: 8,
    },
    walletAvatar: { width: 32, height: 32, borderRadius: T.rSm, alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
    walletAvatarTxt: { fontSize: 13, fontWeight: '600' },
    walletName: { fontSize: 12, fontWeight: '500', color: T.white },
    walletEmail: { fontSize: 9, color: T.gray500, marginTop: 1 },
    walletBals: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
    walletBalLbl: { fontSize: 8, color: T.gray600, textTransform: 'uppercase', letterSpacing: 0.5 },
    walletBalVal: { fontSize: 13, fontWeight: '600' },
    walletBtn: {
        flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5,
        borderWidth: 1, paddingVertical: 7, borderRadius: T.rFull,
    },
    walletBtnTxt: { fontSize: 10, fontWeight: '500' },

    // ── Settings card — mirrors .feature-card
    settingCard: {
        backgroundColor: T.glass,
        borderRadius: T.rMd, borderWidth: 1, borderColor: T.glassBorder,
        padding: 14, marginBottom: 10,
        position: 'relative', overflow: 'hidden',
    },
    settingAccentTop: { position: 'absolute', top: 0, left: 0, right: 0, height: 2 },
    settingCardHead: { flexDirection: 'row', gap: 10, marginBottom: 14, alignItems: 'flex-start' },
    settingIconBox: { width: 28, height: 28, borderRadius: T.rSm, alignItems: 'center', justifyContent: 'center' },
    settingTitle: { fontSize: 13, fontWeight: '500', color: T.white },
    settingDesc: { fontSize: 10, color: T.gray400, marginTop: 2, lineHeight: 15 },
    settingRow: { flexDirection: 'row', gap: 8 },
    settingInputWrap: {
        flex: 1, flexDirection: 'row', alignItems: 'center',
        backgroundColor: T.navyMid, borderWidth: 1, borderColor: T.glassBorder,
        borderRadius: T.rMd, paddingHorizontal: 10,
    },
    settingCcy: { fontSize: 11, color: T.gray500, fontWeight: '500', marginRight: 6 },
    settingInput: { flex: 1, color: T.white, fontSize: 14, paddingVertical: 9 },
    // Update button — mirrors .btn-primary (blue gradient)
    updateBtn: {
        backgroundColor: T.blue,
        paddingHorizontal: 16, paddingVertical: 9,
        borderRadius: T.rMd,
        alignItems: 'center', justifyContent: 'center',
        ...Platform.select({ web: { boxShadow: '0 4px 15px rgba(45,140,240,0.35)' } }),
    },
    updateBtnTxt: { color: T.white, fontSize: 12, fontWeight: '500' },
    settingInfoCard: {
        backgroundColor: T.glass,
        borderRadius: T.rSm, borderWidth: 1, borderColor: T.glassBorder,
        padding: 12, marginBottom: 8,
    },
    settingInfoKey: { fontSize: 9, color: T.gray500, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
    settingInfoVal: { fontSize: 15, fontWeight: '600', color: T.white },
    settingInfoDesc: { fontSize: 10, color: T.gray500, marginTop: 4 },

    // ── Challenge management styles
    createChallengeBtn: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: T.goldLight, paddingHorizontal: 10, paddingVertical: 5, borderRadius: T.rFull,
    },
    createChallengeBtnTxt: { color: T.navy, fontSize: 10, fontWeight: '500' },
    challengeCard: {
        backgroundColor: T.glass, borderRadius: T.rMd, borderWidth: 1, borderColor: T.glassBorder,
        padding: 12, marginBottom: 10, position: 'relative', overflow: 'hidden',
    },
    challengeAccentLine: { position: 'absolute', top: 0, left: 0, right: 0, height: 2 },
    challengeCardHead: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10 },
    challengeIconBox: { width: 34, height: 34, borderRadius: T.rSm, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
    challengeTitle: { fontSize: 13, fontWeight: '500', color: T.white },
    challengeStatsRow: { flexDirection: 'row', backgroundColor: T.glass2, borderRadius: T.rSm, padding: 8, gap: 2, marginBottom: 4 },
    challengeStat: { flex: 1, alignItems: 'center' },
    challengeStatVal: { color: T.white, fontSize: 10, fontWeight: '500' },
    challengeStatLbl: { color: T.gray600, fontSize: 7, marginTop: 1, textAlign: 'center' },
    microPill: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: T.rFull, borderWidth: 1 },
    microPillTxt: { fontSize: 8, fontWeight: '500', letterSpacing: 0.4 },
    statusBtn: {
        paddingHorizontal: 10, paddingVertical: 4, borderRadius: T.rFull,
        borderWidth: 1, backgroundColor: T.glass,
    },
    statusBtnTxt: { fontSize: 9, fontWeight: '500' },
    participantsWrap: { marginTop: 10, borderTopWidth: 1, borderTopColor: T.glassBorder, paddingTop: 10 },
    participantsTitle: { fontSize: 9, fontWeight: '500', color: T.gray400, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 },
    partEmpty: { color: T.gray600, fontSize: 11, textAlign: 'center', paddingVertical: 8 },
    partRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: T.glassBorder, gap: 8 },
    partRank: { color: T.gray500, fontSize: 10, fontWeight: '500', width: 22 },
    partName: { flex: 1, color: T.white, fontSize: 11, fontWeight: '600' },
    partAmt: { color: T.goldLight, fontSize: 11, fontWeight: '500', marginRight: 4 },

    // Modal styles for create challenge
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.75)', justifyContent: 'flex-end' },
    modalSheet: {
        backgroundColor: T.navyLight, borderTopLeftRadius: 20, borderTopRightRadius: 20,
        padding: 20, maxHeight: '85%', borderTopWidth: 1, borderColor: T.glassBorder,
    },
    modalHandle: { width: 38, height: 4, backgroundColor: T.glassBorder, borderRadius: 2, alignSelf: 'center', marginBottom: 18 },
    modalTitle: { color: T.white, fontSize: 16, fontWeight: '600', marginBottom: 16, textAlign: 'center' },
    modalLabel: { color: T.gray400, fontSize: 11, fontWeight: '600', marginBottom: 5 },
    modalInput: {
        borderWidth: 1, borderColor: T.glassBorder, borderRadius: T.rMd,
        padding: 10, color: T.white, fontSize: 13,
        backgroundColor: T.navyMid, marginBottom: 12,
    },
    cadenceBtn: { flex: 1, alignItems: 'center', padding: 8, borderRadius: T.rMd, borderWidth: 1, borderColor: T.glassBorder, backgroundColor: T.glass },
    cadenceBtnActive: { borderColor: 'rgba(245,166,35,0.5)', backgroundColor: 'rgba(245,166,35,0.1)' },
    cadenceBtnTxt: { color: T.gray500, fontSize: 11, fontWeight: '500' },
    iconBtn: { width: 40, height: 40, borderRadius: T.rSm, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: T.glassBorder, backgroundColor: T.glass },
    iconBtnActive: { borderColor: 'rgba(245,166,35,0.5)', backgroundColor: 'rgba(245,166,35,0.1)' },
    modalCancelBtn: { flex: 1, padding: 11, borderRadius: T.rFull, backgroundColor: T.glass, alignItems: 'center', borderWidth: 1, borderColor: T.glassBorder },
    modalConfirmBtn: { flex: 2, padding: 11, borderRadius: T.rFull, backgroundColor: T.gold, alignItems: 'center' },

    // ── Detail Panel UI Styles ────────────────────
    emptyDetailPane: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
    emptyDetailTitle: { fontSize: 14, fontWeight: '500', color: T.gray400, marginTop: 12 },
    emptyDetailSub: { fontSize: 11, color: T.gray600, textAlign: 'center', marginTop: 4, paddingHorizontal: 20 },

    detailPane: { flex: 1, backgroundColor: T.glass, borderRadius: T.rLg, borderWidth: 1, borderColor: T.glassBorder, padding: 16 },
    detailHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: T.glassBorder },
    detailHeaderTitle: { fontSize: 13, fontWeight: '600', color: T.white, letterSpacing: 0.5 },
    
    detailProfile: { alignItems: 'center', marginBottom: 24 },
    detailAvatar: { width: 40, height: 40, borderRadius: T.rMd, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
    detailAvatarTxt: { fontSize: 14, fontWeight: '600' },
    detailName: { fontSize: 14, fontWeight: '600', color: T.white, textAlign: 'center' },
    detailSub: { fontSize: 11, color: T.gray400, marginTop: 2, textAlign: 'center' },
    
    detailBalBox: { backgroundColor: T.glass2, borderRadius: T.rMd, padding: 12, alignItems: 'center', width: '100%', marginTop: 16, borderWidth: 1, borderColor: T.glassBorder },
    detailBalLbl: { fontSize: 9, color: T.gray500, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
    detailBalVal: { fontSize: 16, fontWeight: '600', color: T.white },

    detailSection: { marginTop: 24 },
    detailSectionTitle: { fontSize: 9, color: T.gray500, fontWeight: '600', letterSpacing: 1.5, marginBottom: 12 },
    
    txTypeToggle: { flexDirection: 'row', backgroundColor: T.glassBorder, padding: 4, borderRadius: T.rMd, marginBottom: 16 },
    txTypeBtn: { flex: 1, paddingVertical: 10, alignItems: 'center', borderRadius: T.rSm },
    txTypeBtnActiveT: { backgroundColor: 'rgba(16,185,129,0.1)' },
    txTypeBtnActiveW: { backgroundColor: 'rgba(239,68,68,0.1)' },
    txTypeTxt: { fontSize: 10, fontWeight: '500', color: T.gray400 },

    detailFieldLbl: { fontSize: 10, fontWeight: '600', color: T.gray400, marginBottom: 6 },
    detailInput: { backgroundColor: T.navyMid, borderWidth: 1, borderColor: T.glassBorder, borderRadius: T.rMd, padding: 12, color: T.white, fontSize: 12, marginBottom: 16 },
    detailSubmitBtn: { paddingVertical: 14, borderRadius: T.rMd, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' },
    detailSubmitTxt: { color: T.white, fontSize: 11, fontWeight: '600', letterSpacing: 0.5 },

    // Split Layout — always-on 2-column grid
    splitGrid: {
        width: '100%',
        marginTop: 24,
        flexDirection: 'row',
        minHeight: '100%',
    },
    splitLeft: {
        flex: 1,
        paddingRight: 16,
    },
    columnDivider: {
        width: 1,
        backgroundColor: 'rgba(255,255,255,0.08)',
        alignSelf: 'stretch',
        marginHorizontal: 4,
    },
    splitRight: {
        flex: 1,
        paddingLeft: 16,
    },
});


export { T, S };
